import { Button, Spinner } from '@salesforce/design-system-react';

import { QualityAdjustmentNavigation } from '../../../shared/components';
import { QualityFormulaStoreProvider } from '../../quality-formulas/store/QualityFormulaStoreProvider';
import { useTemplate } from '../hooks/useTemplates';
import { buildInitialWorkspaceState, TEMPLATE_INITIAL_TEST_VALUES } from '../templateWorkspaceState';
import { TemplateEditorForm } from './TemplateEditorForm';

interface TemplateEditorContentProps {
  onClose?: () => void;
  /** `null` renders the form in create mode. */
  templateId: null | string;
  variant?: 'inline' | 'page';
}

/**
 * Loads and seeds the template editor for the routed page or the inline Manage Templates card.
 */
export const TemplateEditorContent: React.FC<TemplateEditorContentProps> = ({
  onClose,
  templateId,
  variant = 'inline',
}) => {
  const { data: template, error, isError, isFetching, isLoading, refetch } = useTemplate(templateId);
  const isLoadingTemplate = isLoading || isFetching;

  if (templateId !== null && (isLoadingTemplate || isError || !template)) {
    const status = isLoadingTemplate ? (
      <div className="slds-grid slds-grid_align-center slds-p-around_x-large">
        <Spinner hasContainer={false} />
      </div>
    ) : (
      <div className="slds-p-around_medium" role="alert">
        <p>Unable to load template: {error?.message ?? 'No template data was returned.'}</p>
        <Button
          label="Retry"
          onClick={() => {
            void refetch();
          }}
          variant="neutral"
        />
      </div>
    );

    return variant === 'page' ? (
      <div
        className="slds-p-bottom_x-large slds-p-horizontal_medium slds-p-top_small"
        style={{ margin: '0 auto', maxWidth: 1180, width: '100%' }}
      >
        <QualityAdjustmentNavigation />
        {status}
      </div>
    ) : (
      status
    );
  }

  const resolvedTemplate = templateId !== null ? template : undefined;
  const initialState = buildInitialWorkspaceState(resolvedTemplate);

  return (
    <QualityFormulaStoreProvider
      initialSpecStates={initialState.specStates}
      initialTestValues={resolvedTemplate ? undefined : TEMPLATE_INITIAL_TEST_VALUES}
      initialVisibleSpecTypes={initialState.visibleSpecTypes}
      key={templateId ?? 'new'}
    >
      <div className={variant === 'inline' ? 'slds-p-around_medium' : undefined}>
        <TemplateEditorForm
          compact={variant === 'inline'}
          onDone={onClose}
          template={resolvedTemplate}
          variant={variant}
        />
      </div>
    </QualityFormulaStoreProvider>
  );
};
